/*
 * User Activity Tracking — the listeners.
 *
 * Renders nothing. Mounted once, inside <BrowserRouter> and next to <ScrollToTop />, which is the
 * only place that both has router context and covers every route — including /login and /signup,
 * where the most interesting part of a journey happens and where the app shell (Layout) is not
 * mounted at all.
 *
 * It produces the six event types the backend understands and hands them to the tracking client,
 * which owns the buffer and the transport (src/services/trackingApi).
 *
 *   session_start  once per tab session, guarded by sessionStorage so a reload does not repeat it
 *   page_view      on every route change, with the route PATTERN so dashboards can group
 *   click          delegated, throttled, and only for elements worth naming
 *   scroll         once per milestone per page view
 *   session_end    once, as the page goes away, carrying the session's duration
 *
 * Per-page dwell time is deliberately NOT sent: consecutive events in a session are already ordered
 * by the {sessionId, ts} index server-side, so it is a difference between two rows rather than a
 * number to measure, get wrong, and store on every page view.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { flushTracking, trackEvent } from 'src/services/trackingApi'
import {
  CLICK_TARGET_SELECTOR,
  CLICK_THROTTLE_MS,
  MAX_CLICKS_PER_PAGE_VIEW,
  MAX_LABEL_LENGTH,
  SCROLL_MILESTONES,
} from 'src/services/trackingApi/constants'
import { matchRoutePattern } from 'src/utils/matchRoutePattern'

const SESSION_STARTED_AT_KEY = 'trackingSessionStartedAt'

const readSessionStartedAt = (): number => {
  try {
    const existing = window.sessionStorage.getItem(SESSION_STARTED_AT_KEY)

    if (existing) {
      return Number(existing) || 0
    }

    const now = Date.now()
    window.sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(now))

    return 0
  } catch (error) {
    return 0
  }
}

/** How far down the page the viewport's bottom edge has reached, in percent. */
const getScrollDepth = (): number => {
  const scrollHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body ? document.body.scrollHeight : 0
  )
  const viewport = window.innerHeight

  // Nothing to scroll: report 0 rather than a misleading 100.
  if (!scrollHeight || scrollHeight <= viewport) {
    return 0
  }

  return Math.min(
    100,
    Math.round(((window.scrollY + viewport) / scrollHeight) * 100)
  )
}

const describeClickTarget = (element: Element): Record<string, string> => {
  const tag = element.tagName.toLowerCase()
  const label =
    element.getAttribute('data-track') || element.getAttribute('aria-label')

  const metadata: Record<string, string> = { tag }

  if (label) {
    metadata.label = label.trim().slice(0, MAX_LABEL_LENGTH)
  }

  const href = element.getAttribute('href')

  if (href && href.startsWith('/')) {
    metadata.href = href.split('?')[0].split('#')[0]
  }

  return metadata
}

export const ActivityTracker = () => {
  const { pathname } = useLocation()
  const currentPathRef = useRef<string | undefined>(undefined)
  const lastClickAtRef = useRef(0)
  const clickCountRef = useRef(0)
  const milestonesRef = useRef<number[]>([])
  const sessionEndedRef = useRef(false)

  useEffect(() => {
    if (currentPathRef.current === pathname) {
      return
    }

    const referrer = currentPathRef.current || document.referrer || undefined

    currentPathRef.current = pathname
    clickCountRef.current = 0
    milestonesRef.current = []

    trackEvent({
      eventType: 'page_view',
      path: pathname,
      referrer,
      route: matchRoutePattern(pathname),
    })
  }, [pathname])

  useEffect(() => {
    const startedAt = readSessionStartedAt()

    if (!startedAt) {
      trackEvent({
        eventType: 'session_start',
        metadata: { referrer: document.referrer || 'direct' },
      })
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null

      if (!target || typeof target.closest !== 'function') {
        return
      }

      const element = target.closest(CLICK_TARGET_SELECTOR)

      if (!element) {
        return
      }

      const now = Date.now()

      if (
        now - lastClickAtRef.current < CLICK_THROTTLE_MS ||
        clickCountRef.current >= MAX_CLICKS_PER_PAGE_VIEW
      ) {
        return
      }

      lastClickAtRef.current = now
      clickCountRef.current += 1

      trackEvent({
        eventType: 'click',
        metadata: describeClickTarget(element),
        path: currentPathRef.current,
      })
    }

    let scrollScheduled = false

    const measureScroll = () => {
      scrollScheduled = false

      const depth = getScrollDepth()

      SCROLL_MILESTONES.forEach((milestone) => {
        if (
          depth < milestone ||
          milestonesRef.current.indexOf(milestone) !== -1
        ) {
          return
        }

        milestonesRef.current.push(milestone)
        trackEvent({
          eventType: 'scroll',
          metadata: { depth: milestone },
          path: currentPathRef.current,
        })
      })
    }

    const handleScroll = () => {
      if (scrollScheduled) {
        return
      }

      scrollScheduled = true
      window.requestAnimationFrame(measureScroll)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushTracking()
      }
    }

    const handlePageHide = () => {
      if (!sessionEndedRef.current) {
        sessionEndedRef.current = true

        const sessionStartedAt = readSessionStartedAt()

        trackEvent({
          durationMs: sessionStartedAt
            ? Date.now() - sessionStartedAt
            : undefined,
          eventType: 'session_end',
          path: currentPathRef.current,
        })
      }

      flushTracking(true)
    }

    document.addEventListener('click', handleClick, true)
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      document.removeEventListener('click', handleClick, true)
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      flushTracking()
    }
  }, [])

  return null
}
